import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const UNIT_KEYS = { clean: "Every clean", days: "Every N days", weeks: "Every N weeks", months: "Every N months" };

// Non-recursive row (recursion is done in data via flattenTasks) so the
// visual-edits babel plugin doesn't choke on a self-referential component.
export function TaskRow({ node, depth, isParent, onAddChild, onUpdate, onRemove }) {
  const { t } = useTranslation();
  const sched = node.sched || { unit: "clean", interval: 1 };
  const applies = node.applies_to || "both";

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div className="rounded-md border border-border bg-card p-2">
        <div className="flex items-center gap-2">
          <Input
            value={node.name}
            onChange={(e) => onUpdate(node.id, { name: e.target.value })}
            className="h-8 flex-1"
            data-testid={`task-name-${node.id}`}
          />
          {!isParent && (
            <>
              <div className="flex items-center gap-1">
                <span className="label-caps text-indigo-600">{t("Full")}</span>
                <Input
                  type="number"
                  min="0"
                  value={node.est_full}
                  onChange={(e) => onUpdate(node.id, { est_full: Number(e.target.value) || 0 })}
                  className="h-8 w-14"
                  data-testid={`task-full-${node.id}`}
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="label-caps text-lime-600">{t("Svc")}</span>
                <Input
                  type="number"
                  min="0"
                  value={node.est_service}
                  onChange={(e) => onUpdate(node.id, { est_service: Number(e.target.value) || 0 })}
                  className="h-8 w-14"
                  data-testid={`task-service-${node.id}`}
                />
              </div>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onAddChild(node.id)}
            data-testid={`add-subtask-${node.id}`}
            title={t("Add sub-task")}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive"
            onClick={() => onRemove(node.id)}
            data-testid={`remove-task-${node.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {!isParent && (
          <div className="mt-2 flex flex-wrap items-center gap-2 pl-1">
            <span className="label-caps text-muted-foreground">{t("Applies")}</span>
            <Select value={applies} onValueChange={(v) => onUpdate(node.id, { applies_to: v })}>
              <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`applies-${node.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">{t("Full & Service")}</SelectItem>
                <SelectItem value="full">{t("Full only")}</SelectItem>
                <SelectItem value="service">{t("Service only")}</SelectItem>
              </SelectContent>
            </Select>

            <span className="label-caps ml-1 text-muted-foreground">{t("Cadence")}</span>
            <Select
              value={sched.unit}
              onValueChange={(v) => onUpdate(node.id, { sched: { unit: v, interval: sched.interval || 1 } })}
            >
              <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`cadence-unit-${node.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(UNIT_KEYS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {t(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sched.unit !== "clean" && (
              <Input
                type="number"
                min="1"
                value={sched.interval || 1}
                onChange={(e) =>
                  onUpdate(node.id, { sched: { unit: sched.unit, interval: Number(e.target.value) || 1 } })
                }
                className="h-7 w-14 text-xs"
                data-testid={`cadence-interval-${node.id}`}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
